<template>
  <div class="page-container storefront-template-settings-page">
    <div class="page-header">
      <h1 class="page-title">商城前台模板設定</h1>
      <div class="page-header-actions">
        <button type="button" class="btn btn-secondary" :disabled="loading || saving" @click="restoreFactoryDefaults">
          還原為系統預設結構
        </button>
        <button type="button" class="btn btn-primary" :disabled="loading || saving" @click="handleSave">
          <span v-if="saving" class="spinner-sm"></span>
          {{ saving ? '儲存中…' : '儲存' }}
        </button>
      </div>
    </div>

    <p class="page-lead">
      設定全站商店「外觀與版型」可選的設計模板分類與代碼，以及<strong>各模板底下可多組「主題（配色）」代碼</strong>（對應前台預先撰寫的
      CSS／設計 token）。客戶後台僅能從此清單選取；代碼須與前台專案實作一致（詳見
      <code>docs/development/商城前台設計模板開發規範.md</code>）。
    </p>

    <div v-if="loadError" class="error-state">
      <p>{{ loadError }}</p>
      <button type="button" class="btn btn-secondary" @click="load">重試</button>
    </div>

    <div v-else-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中…</p>
    </div>

    <div v-else class="settings-stack">
      <div v-if="saveOk" class="alert alert-success">已儲存</div>
      <div v-if="saveError" class="alert alert-error">{{ saveError }}</div>

      <div v-for="(cat, ci) in categoriesDraft" :key="cat.id" class="category-panel">
        <div class="category-head">
          <h2>分類 {{ ci + 1 }}</h2>
          <button type="button" class="btn-text-danger" @click="removeCategory(ci)">移除此分類</button>
        </div>
        <div class="form-row-3">
          <div class="form-group">
            <label>分類代碼（id）<span class="req">*</span></label>
            <input v-model="cat.id" class="form-control" placeholder="例：general" />
            <small class="hint">小寫英文開頭，僅 a-z、0-9、底線</small>
          </div>
          <div class="form-group">
            <label>分類名稱<span class="req">*</span></label>
            <input v-model="cat.label" class="form-control" />
          </div>
          <div class="form-group form-group--span2">
            <label>分類說明</label>
            <input v-model="cat.description" class="form-control" />
          </div>
        </div>

        <h3 class="sub-title">此分類下的模板（{{ cat.templates.length }} 筆）</h3>
        <div v-for="(t, ti) in cat.templates" :key="ti" class="template-block">
          <div class="template-block-head">
            <span class="template-block-title">模板 {{ ti + 1 }}</span>
            <button type="button" class="btn-icon" title="刪除此模板" @click="removeTemplate(ci, ti)">
              <i class="ri-delete-bin-line"></i>
            </button>
          </div>
          <div class="form-row-template">
            <div class="form-group">
              <label>模板代碼（value）<span class="req">*</span></label>
              <input v-model="t.value" class="form-control form-control-sm" placeholder="standard" />
              <small class="hint">小寫英數與底線；與前台目錄／registry 一致</small>
            </div>
            <div class="form-group">
              <label>顯示名稱<span class="req">*</span></label>
              <input v-model="t.label" class="form-control form-control-sm" />
            </div>
            <div class="form-group form-group--full">
              <label>說明</label>
              <input v-model="t.description" class="form-control form-control-sm" />
            </div>
          </div>

          <div class="themes-panel">
            <div class="themes-panel-head">
              <h4 class="themes-title">主題（配色）</h4>
              <span class="themes-count">{{ themeRows(t).length }} 組</span>
            </div>
            <p class="hint themes-lead">
              每組代碼對應前台一組預先設計的樣式（例如獨立 CSS 或 SCSS 變體）。留空儲存時後端會套用系統預設四組；客戶後台僅能擇一。
            </p>
            <div class="table-wrap">
              <table class="data-table data-table--themes">
                <thead>
                  <tr>
                    <th>主題代碼<span class="req">*</span></th>
                    <th>顯示名稱<span class="req">*</span></th>
                    <th>說明</th>
                    <th>預覽（CSS 背景值）</th>
                    <th width="56"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(th, thi) in themeRows(t)" :key="thi">
                    <td>
                      <input v-model="th.value" class="form-control form-control-sm" placeholder="default" />
                    </td>
                    <td>
                      <input v-model="th.label" class="form-control form-control-sm" />
                    </td>
                    <td>
                      <input v-model="th.description" class="form-control form-control-sm" />
                    </td>
                    <td>
                      <input v-model="th.preview" class="form-control form-control-sm" placeholder="linear-gradient(...)" />
                    </td>
                    <td>
                      <button type="button" class="btn-icon" title="刪除主題" @click="removeTheme(ci, ti, thi)">
                        <i class="ri-delete-bin-line"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <button type="button" class="btn btn-sm btn-secondary" @click="addTheme(ci, ti)">新增主題</button>
          </div>
        </div>
        <button type="button" class="btn btn-sm btn-secondary" @click="addTemplate(ci)">新增模板</button>
      </div>

      <button type="button" class="btn btn-secondary" @click="addCategory">新增分類</button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { usePageTitle } from '@/composables/usePageTitle.js'
import { systemCommerceStorefrontApi } from '@/api/modules/systemCommerceStorefront.js'
import { DEFAULT_STOREFRONT_TEMPLATE_CATEGORIES } from '@/constants/storefrontTemplateCategoriesDefault.js'
import { cloneBaseStorefrontThemePresets } from '@/constants/storefrontTemplateThemePresets.js'

usePageTitle('商城前台模板設定')

const loading = ref(true)
const saving = ref(false)
const loadError = ref('')
const saveError = ref('')
const saveOk = ref(false)
const categoriesDraft = ref([])
const factoryDefaults = ref(null)

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj))
}

/** 編輯草稿：舊資料若無 themes，補上與後端 normal 化一致之預設（不在 render 內副作用） */
function normalizeDraftTemplateThemes(list) {
  if (!Array.isArray(list)) return
  for (const cat of list) {
    if (!Array.isArray(cat.templates)) continue
    for (const t of cat.templates) {
      if (!Array.isArray(t.themes) || !t.themes.length) {
        t.themes = cloneBaseStorefrontThemePresets()
      }
    }
  }
}

function load() {
  loading.value = true
  loadError.value = ''
  systemCommerceStorefrontApi
    .getTemplateCategories()
    .then((res) => {
      const d = res?.data
      const list = d?.categories?.length ? d.categories : d?.defaultCategories
      factoryDefaults.value = d?.defaultCategories?.length
        ? deepClone(d.defaultCategories)
        : deepClone(DEFAULT_STOREFRONT_TEMPLATE_CATEGORIES)
      categoriesDraft.value = Array.isArray(list) && list.length ? deepClone(list) : deepClone(factoryDefaults.value)
      normalizeDraftTemplateThemes(categoriesDraft.value)
    })
    .catch((e) => {
      loadError.value = e?.response?.data?.message ?? '載入失敗'
    })
    .finally(() => {
      loading.value = false
    })
}

function themeRows(t) {
  return t.themes || []
}

function addCategory() {
  const id = `new_cat_${Date.now().toString(36)}`
  categoriesDraft.value.push({
    id,
    label: '新分類',
    description: '',
    templates: [
      {
        value: 'new_template',
        label: '新模板',
        description: '',
        themes: cloneBaseStorefrontThemePresets(),
      },
    ],
  })
}

function removeCategory(index) {
  if (categoriesDraft.value.length <= 1) {
    saveError.value = '至少需保留一個分類'
    return
  }
  categoriesDraft.value.splice(index, 1)
  saveError.value = ''
}

function addTemplate(catIndex) {
  categoriesDraft.value[catIndex].templates.push({
    value: `tpl_${Date.now().toString(36)}`,
    label: '新模板',
    description: '',
    themes: cloneBaseStorefrontThemePresets(),
  })
}

function addTheme(catIndex, tplIndex) {
  const t = categoriesDraft.value[catIndex].templates[tplIndex]
  if (!Array.isArray(t.themes)) t.themes = []
  t.themes.push({
    value: `theme_${Date.now().toString(36)}`,
    label: '新主題',
    description: '',
    preview: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  })
}

function removeTheme(catIndex, tplIndex, themeIndex) {
  const t = categoriesDraft.value[catIndex].templates[tplIndex]
  if (!t.themes || t.themes.length <= 1) {
    saveError.value = '每個模板至少需保留一組主題（配色）'
    return
  }
  t.themes.splice(themeIndex, 1)
  saveError.value = ''
}

function removeTemplate(catIndex, tplIndex) {
  const cat = categoriesDraft.value[catIndex]
  if (cat.templates.length <= 1) {
    saveError.value = '每個分類至少需保留一筆模板'
    return
  }
  cat.templates.splice(tplIndex, 1)
  saveError.value = ''
}

function restoreFactoryDefaults() {
  if (!factoryDefaults.value?.length) {
    categoriesDraft.value = deepClone(DEFAULT_STOREFRONT_TEMPLATE_CATEGORIES)
  } else {
    categoriesDraft.value = deepClone(factoryDefaults.value)
  }
  normalizeDraftTemplateThemes(categoriesDraft.value)
  saveError.value = ''
}

async function handleSave() {
  saving.value = true
  saveError.value = ''
  saveOk.value = false
  try {
    await systemCommerceStorefrontApi.putTemplateCategories(categoriesDraft.value)
    saveOk.value = true
    window.setTimeout(() => {
      saveOk.value = false
    }, 3000)
    await load()
  } catch (e) {
    saveError.value = e?.response?.data?.message ?? '儲存失敗'
  } finally {
    saving.value = false
  }
}

onMounted(() => load())
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.storefront-template-settings-page .page-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.page-header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.page-lead {
  color: #4b5563;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 20px;
  max-width: 960px;
}
.page-lead code {
  font-size: 12px;
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
}
.settings-stack {
  max-width: 1100px;
}
.category-panel {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fafafa;
}
.category-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.category-head h2 {
  margin: 0;
  font-size: 16px;
}
.sub-title {
  font-size: 14px;
  margin: 16px 0 8px;
  color: #374151;
}
.template-block {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 14px 14px;
  margin-bottom: 12px;
  background: #fff;
}
.template-block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.template-block-title {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}
.form-row-template {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 12px;
  margin-bottom: 12px;
}
.form-group--full {
  grid-column: 1 / -1;
}
.themes-panel {
  border-top: 1px dashed #e5e7eb;
  padding-top: 12px;
  margin-top: 4px;
}
.themes-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}
.themes-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
}
.themes-count {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}
.themes-lead {
  margin: 0 0 8px 0;
}
.data-table--themes {
  min-width: 720px;
}
.form-row-3 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.form-group--span2 {
  grid-column: 1 / -1;
}
.form-group label {
  display: block;
  font-size: 13px;
  margin-bottom: 4px;
  color: #374151;
}
.req {
  color: #dc2626;
}
.hint {
  display: block;
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}
.table-wrap {
  overflow-x: auto;
  margin-bottom: 8px;
}
.data-table {
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;
  font-size: 13px;
}
.data-table th,
.data-table td {
  border: 1px solid #e5e7eb;
  padding: 8px;
  text-align: left;
  vertical-align: middle;
}
.data-table th {
  background: #f9fafb;
  font-weight: 600;
}
.btn-text-danger {
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
  font-size: 13px;
}
.btn-text-danger:hover {
  text-decoration: underline;
}
.btn-icon {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 4px 8px;
  cursor: pointer;
  color: #6b7280;
}
.btn-icon:hover {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fecaca;
}
.btn-sm {
  font-size: 13px;
  padding: 6px 12px;
}
.alert {
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 14px;
}
.alert-success {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}
.alert-error {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}
.spinner-sm {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #fff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  vertical-align: middle;
  margin-right: 6px;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.error-state {
  padding: 24px;
  color: #b91c1c;
}
.loading-state {
  padding: 40px;
  text-align: center;
  color: #6b7280;
}
</style>
